import { TextboxWrapper } from "@/src/common/TextboxWrapper";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { AjaxMethod } from "@Components/Modules/Ajax";
import type { ITextbox } from "@Components/Textbox";
import { InputMask } from "@react-input/mask";

import type { IInputTracker } from "../telemetry/InputTracker";
import { SetInputTrackerProps } from "../telemetry/InputTracker";

import * as css from "./AccountNumber.module.css";

interface AccountNumberState extends IComponentAjaxState {
    accountNumber: string;
    bankName: string;
}

export interface IAccountNumber extends IComponentAjax<string> {
    onChange: (accountNumberValue: string, validBankInfo: boolean) => boolean | void;
    accountNumberTextbox: IInputTracker<ITextbox>;
    accountNumberLabel: string;
    bankName?: string;
}

export class AccountNumber extends ComponentAjax<string, IAccountNumber, AccountNumberState> {
    protected get renderInputText() {
        if (!this.props.accountNumberTextbox) {
            return null;
        }

        const accountNumberTextbox = SetInputTrackerProps({
            ...this.props.accountNumberTextbox,
            clientId: this.props.accountNumberTextbox.clientId,
            focusTelemetryCustom: { eventName: "cshbck-wthdrAccountEdit", actionType: "Undefined" },
            properties: {
                ...this.props.accountNumberTextbox.properties,
                onChange: this.onChange,
                value: this.state.accountNumber,
            },
        });

        return (
            <>
                <label>{this.props.accountNumberLabel}</label>
                {this.props.children ?? (
                    <InputMask
                        component={TextboxWrapper}
                        mask="__ ____ ____ ____ ____ ____ ____"
                        replacement={{ _: /\d/ }}
                        {...accountNumberTextbox}
                    />
                )}
            </>
        );
    }

    public static readonly defaultProps: IAccountNumber = {
        onChange: null,
        accountNumberTextbox: null,
        accountNumberLabel: null,
        ajaxMethod: AjaxMethod.Post,
    };

    constructor(props: IAccountNumber) {
        super({ ...props, ajaxMethod: AjaxMethod.Post });

        this.state = {
            isLoading: false,
            accountNumber: this.formatAccountNumber(this.props.accountNumberTextbox.properties.value),
            bankName: this.props.bankName,
        };
    }

    public render() {
        return (
            <div {...this.rootDefaultAttributes(css.accountNumberComponent)}>
                {this.renderInputText}
                {this.state.bankName && <div className={css.bankInfo}>{this.state.bankName}</div>}
            </div>
        );
    }

    protected ajaxSuccess(response: string): void {
        this.setState({ bankName: response });

        if (this.props.onChange) {
            this.props.onChange(this.state.accountNumber, !!response);
        }
    }

    protected ajaxFail() {
        return false;
    }

    private formatAccountNumber(account: string) {
        if (!account) {
            return "";
        }

        const value = account.replace(/[^0-9]/gi, "");
        const parts = [];

        parts.push(value.substring(0, 2));

        for (let r = 2; r < value.length; r += 4) {
            parts.push(value.substring(r, r + 4));
        }

        if (parts.length) {
            return this.formatMaxLength(parts.join(" "));
        }
        return this.formatMaxLength(value);
    }

    private formatMaxLength(value: string): string {
        const { maxLength } = this.props.accountNumberTextbox.properties;

        if (maxLength) {
            return value.substring(0, maxLength);
        }

        return value;
    }

    private onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formattedValue = this.formatAccountNumber(event.target.value);

        this.setState({ accountNumber: formattedValue, bankName: null });

        if (formattedValue.length === this.props.accountNumberTextbox.properties.maxLength) {
            this.ajaxRequest({ accountNumber: formattedValue.replace(/\s/g, "") });
            return false;
        }

        if (this.props.onChange) {
            this.props.onChange(formattedValue, true);
        }

        return false;
    };
}
